import type {
  ApiDashboardSchema,
  ApiReturnCampaign,
  ApiReturnMeasureType,
  IntervalJSON,
  ReportStructure,
  SourceMap,
} from '@highimpact-frontend/api-types';
import { TimeRangeUnionForm } from '@highimpact/time-interval-selector';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { useDashboardHost } from '../context/DashboardHostContext';
import type { CreateReportFormProps } from '../context/DashboardHostContext.types';
import * as R from '../internal/utils/record';
import { useCampaignsQuery } from '../query/useCampaignsQuery';
import { useMeasureTypesQuery } from '../query/useMeasureTypesQuery';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../runtime/DashboardLibraryProvider';
import { hydrateDashboardLibraryState } from '../state/actions';
import type { DashboardGridType } from '../state/dashboard/initials';
import {
  buildDashboardStructureFromReportStructure,
  buildMeasureTypeIdToUnitFromMeasureTypes,
  collectSourceLabelPairsFromReportStructure,
} from '../state/report-structure-operations';
import { isPathWithMeasurePoint } from '../state/sourcePathGuards';
import { addDashboardSafely } from '../state/store-operations';

type Status = 'idle' | 'submitting' | 'error' | 'success';

export type ReportDashboardFormComponentProps = CreateReportFormProps & {
  readonly site_id?: number;
  readonly project_id?: number;
};

function withSiteId(
  siteId: number,
  dashboard: ApiDashboardSchema
): ApiDashboardSchema {
  return {
    ...dashboard,
    site_id: siteId,
  };
}

function ReportDashboardFormFields({
  site_id,
  project_id,
  measure_types,
  campaigns,
  onDashboardCreated,
}: {
  site_id: number;
  project_id: number;
  measure_types: readonly ApiReturnMeasureType[];
  campaigns: readonly ApiReturnCampaign[];
  onDashboardCreated: CreateReportFormProps['onDashboardCreated'];
}) {
  const host = useDashboardHost();
  const dispatch = useDashboardLibraryDispatch();
  const libraryState = useDashboardLibrarySelector((state) => state);

  const startDate =
    site_id === 7
      ? new Date('2024-04-09T21:45:00Z')
      : new Date('2024-08-01T00:00:00Z');
  const endDate =
    site_id === 7
      ? new Date('2024-04-10T10:30:00Z')
      : new Date('2024-09-01T00:00:00Z');

  const [name, setName] = useState<string>('');
  const [status, setStatus] = useState<Status>('idle');
  const [siteReportModels, setSiteReportModels] = useState<
    Awaited<ReturnType<typeof host.api.report.getReportModelsBySite>>
  >([]);
  const [reportModelId, setReportModelId] = useState<number>(0);
  const defaultCampaignId = campaigns[0]?.id ?? 0;
  const [campaignId, setCampaignId] = useState<number>(defaultCampaignId);
  const [intervals, setIntervals] = useState<IntervalJSON[]>([]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success';
  }>({ open: false, message: '', severity: 'error' });
  const [formError, setFormError] = useState<{
    hasError: boolean;
    nameErrorMessage: string;
  }>({
    hasError: false,
    nameErrorMessage: '',
  });

  useEffect(() => {
    host.api.report.getReportModelsBySite(site_id).then((models) => {
      setSiteReportModels(models);
      if (models.length) {
        setReportModelId(models[0]!.id);
        setName(`${models[0]!.name}`);
      }
    });
  }, [host.api.report, site_id]);

  const notify = (message: string, severity: 'error' | 'success') => {
    host.showNotification(message, severity);
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const handleNameInput: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setName(e.target.value);
    setFormError({
      hasError: false,
      nameErrorMessage: '',
    });
    setStatus('idle');
  };

  const handleCreateDashboard = async () => {
    setStatus('submitting');

    let layouts: DashboardGridType['layouts']['lg'];
    let structure: ReportStructure;
    try {
      const res = await host.api.report.getReportModelStructure(
        project_id,
        site_id,
        reportModelId,
        {
          campaignId: campaignId === 0 ? null : campaignId,
          intervals: intervals.length === 0 ? null : intervals,
        }
      );
      layouts = res.layouts;
      structure = res.report_structure;
    } catch {
      setStatus('error');
      notify(
        'Création impossible : données du site incompatibles avec ce type de rapport',
        'error'
      );
      return;
    }

    let measureTypeToUnit: ReturnType<
      typeof buildMeasureTypeIdToUnitFromMeasureTypes
    >;
    let sourceMap: SourceMap;
    try {
      const measurePointPaths = structure.selections.filter(
        isPathWithMeasurePoint
      );
      const labelPairs = collectSourceLabelPairsFromReportStructure(structure);
      sourceMap =
        labelPairs.length === 0
          ? {}
          : await host.api.sources.getLabels(labelPairs, {
              project_id,
              site_id,
            });
      measureTypeToUnit = buildMeasureTypeIdToUnitFromMeasureTypes(
        measurePointPaths,
        [...measure_types]
      );
    } catch {
      setStatus('error');
      notify(
        'Création impossible : les libellés ou unités des sources n’ont pas pu être résolus',
        'error'
      );
      return;
    }

    let dashboardData: ApiDashboardSchema;
    let mapping: Record<string, string>;
    try {
      const [mapResult, dbData] = buildDashboardStructureFromReportStructure(
        sourceMap,
        layouts,
        measureTypeToUnit,
        name,
        structure
      );
      dashboardData = withSiteId(site_id, dbData);
      mapping = mapResult;
    } catch {
      setStatus('error');
      notify(
        "Création impossible : le tableau de bord n'a pas pu se générer à partir de ce modèle",
        'error'
      );
      return;
    }

    let dashboard_id: number;
    let dashboardName: string;
    let persistedDashboard: ApiDashboardSchema;
    try {
      persistedDashboard = await host.api.dashboard.postDashboard(
        dashboardData
      );
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dashboard_id = persistedDashboard.id!;
      dashboardName = persistedDashboard.name;
    } catch (err: unknown) {
      const statusCode =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'status' in err.response
          ? (err.response as { status?: number }).status
          : undefined;
      if (statusCode === 409) {
        setFormError({
          hasError: true,
          nameErrorMessage: 'Ce nom de rapport existe déjà pour ce site',
        });
        setStatus('error');
        notify('Ce nom de rapport existe déjà pour ce site', 'error');
        return;
      }
      setStatus('error');
      notify(
        "Création impossible : le tableau de bord du rapport n'a pas pu être sauvegardé",
        'error'
      );
      return;
    }

    try {
      const reversedMapping = R.swapKeysAndValues(mapping);
      const dep = {
        sourceLabelMap: sourceMap,
        startDate,
        endDate,
        w: 12,
        h: 9,
        reportStructure: structure,
        elementNameToVariableName: reversedMapping,
        layouts,
      };
      const updatedState = addDashboardSafely(
        dep,
        'reports',
        { ...persistedDashboard, id: dashboard_id },
        libraryState
      );
      dispatch(hydrateDashboardLibraryState(updatedState));
    } catch {
      setStatus('error');
      notify(
        "Création impossible : le tableau de bord du rapport n'a pas pu se charger dans l'application",
        'error'
      );
      return;
    }

    try {
      onDashboardCreated({
        dashboardName,
        dashboard_id,
        site_id,
        project_id,
      });
      setStatus('success');
      notify('Tableau de bord créé avec succès', 'success');
    } catch {
      setStatus('error');
      notify('Erreur de redirection. Veuillez recharger la page', 'error');
    }
  };

  const renderButtonIcon = () => {
    switch (status) {
      case 'submitting':
        return <HourglassEmptyIcon fontSize="small" />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <SendIcon />;
    }
  };

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        void handleCreateDashboard();
      }}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
    >
      <TextField
        label="Nom"
        value={name}
        onChange={handleNameInput}
        error={formError.hasError}
        helperText={formError.hasError ? formError.nameErrorMessage : undefined}
        required
      />

      <FormControl fullWidth>
        <InputLabel id="report-model-label">Modèle</InputLabel>
        <Select
          labelId="report-model-label"
          value={reportModelId}
          label="Modèle"
          onChange={(e) => {
            const id = Number(e.target.value);
            const sel = siteReportModels.find((m) => m.id === id);
            setReportModelId(id);
            if (sel) setName(sel.name);
          }}
          disabled={!siteReportModels.length}
        >
          {siteReportModels.length === 0 ? (
            <MenuItem value="">
              <em>Aucun modèle disponible</em>
            </MenuItem>
          ) : (
            siteReportModels.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      <FormControl>
        <TimeRangeUnionForm
          onChange={(nextIntervals: IntervalJSON[]) => {
            setIntervals(nextIntervals);
          }}
          campaigns={[...campaigns]}
          noDivision={true}
        />
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        startIcon={
          status === 'submitting' ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            renderButtonIcon()
          )
        }
        disabled={
          status === 'submitting' ||
          !name.trim() ||
          siteReportModels.length === 0
        }
        onClick={() => void handleCreateDashboard()}
        sx={{
          alignSelf: 'center',
        }}
      >
        Créer
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default function ReportDashboardFormComponent({
  onDashboardCreated,
  site_id: siteIdProp,
  project_id: projectIdProp,
}: ReportDashboardFormComponentProps): JSX.Element {
  const host = useDashboardHost();
  const site_id = siteIdProp ?? host.site_id;
  const project_id = projectIdProp ?? host.project_id;
  const { campaigns, loading: campaignsLoading } = useCampaignsQuery(site_id);
  const { measureTypes, loading: measureTypesLoading } = useMeasureTypesQuery();

  if (site_id === null || project_id === null) {
    return <div>Projet ou site inconnu</div>;
  }

  if (campaignsLoading || measureTypesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <ReportDashboardFormFields
      site_id={site_id}
      project_id={project_id}
      measure_types={measureTypes}
      campaigns={campaigns}
      onDashboardCreated={onDashboardCreated}
    />
  );
}
